import {type} from '../../core/utils/types';
import {Action} from '@ngrx/store';
import {User} from '../../core/model/user';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {FeatureToggleModel} from '@app/views/common/feature-toggles/featue-toggle.model';
import {PIIShieldLevelConfig} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {PassportLoginIDP} from '@app/views/sso/identity-providers/idp-settings/passport-login-idp/passport-login-idp.model';

export const AuthActionTypes = {
    AUTHENTICATE: type('[auth] Authenticate'),
    AUTHENTICATE_ERROR: type('[auth] Authentication error'),
    SEND_CODE_AGAIN: type('[auth] Send 1-time token Again'),
    AUTHENTICATE_MFA_FIRST_SUCCESS: type('[auth] Authentication MFA First Success'),
    AUTHENTICATE_SETUP_MFA_FIRST_SUCCESS: type('[auth] Authentication setup MFA First Success'),
    AUTHENTICATE_SUCCESS: type('[auth] Authentication success'),
    AUTHENTICATED: type('[auth] Authenticated'),
    AUTHENTICATED_ERROR: type('[auth] Authenticated error'),
    AUTHENTICATED_SUCCESS: type('[auth] Authenticated success'),
    AUTHENTICATED_USER_REFRESH: type('[auth] Authenticated user refresh data'),
    AUTHENTICATED_USER_REFRESH_SUCCESS: type('[auth] Authenticated user refresh data success'),
    AUTHENTICATED_USER_REFRESH_TOKEN: type('[auth] Authenticated user refresh token'),
    AUTHENTICATED_USER_REFRESH_TOKEN_SUCCESS: type('[auth] Authenticated user refresh token success'),
    // AUTHENTICATED_EXTRA: type("[auth] Authenticated extra"),
    AUTHENTICATED_EXTRA_SUCCESS: type('[auth] Authenticated extra success'),
    AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES: type('[auth] Authenticated update extra user preferences'),
    AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCE_ITEM: type('[auth] Authenticated update extra user preference item'),
    AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES_SUCCESS: type('[auth] Authenticated update extra user preferences success'),
    AUTHENTICATED_EXTRA_ERROR: type('[auth] Authenticated extra error'),
    AUTHENTICATED_EXTRA_UPDATED: type('[auth] Authenticated extra updated'),
    CHECK_ACCEPTED_PRIVACY_POLICY: '[auth] Check accepted Privacy Policy',
    SHOW_CONTENT: '[auth] Show authenticated user\'s content',
    HIDE_CONTENT: '[auth] Hide authenticated user\'s content',
    SHOW_MENU: '[auth] Show navigation menu',
    HIDE_MENU: '[auth] Hide navigation menu',
    LOAD_VENDOR_APPS: type('[auth] Load vendor apps'),
    UPDATE_USER_APPS: type('[auth] Update vendor apps'),
    SIGN_OUT: type('[auth] Sign out'),
    SIGN_OUT_CLEAR_DATA: type('[auth] Sign out clear data'),
    SIGN_OUT_ERROR: type('[auth] Sign out error'),
    SIGN_OUT_SUCCESS: type('[auth] Sign out success'),
    UPDATE_STATE_FROM_LOCAL_STORAGE: type('[auth] Update state from local storage'),
    GET_USER_ORGANIZATION: type('[auth] Get user organization'),
    SET_USER_ORGANIZATION: type('[auth] Set user organization'),
    GET_DISTRICT_FEATURE_TOGGLES: type('[auth] Getting district feature toggles'),
    SET_DISTRICT_FEATURE_TOGGLES_SUCCESS: type('[auth] Set district feature toggles'),
    GET_DISTRICT_FEATURE_TOGGLES_FAILED: type('[auth] Getting district feature toggles failed'),
    GET_VENDOR_FEATURE_TOGGLES: type('[auth] Getting vendor feature toggles'),
    SET_VENDOR_FEATURE_TOGGLES_SUCCESS: type('[auth] Set vendor feature toggles'),
    GET_VENDOR_FEATURE_TOGGLES_FAILED: type('[auth] Getting vendor feature toggles failed'),
    GET_PII_SHIELD_LEVELS_CONFIG: type('[auth] Getting Privacy Shield levels config'),
    SET_PII_SHIELD_LEVELS_CONFIG: type('[auth] Set Privacy Shield levels config'),
    GET_PASSPORT_LOGIN_IDP_LIST: type('[auth] Getting SchoolDay Login IDP list'),
    SET_PASSPORT_LOGIN_IDP_LIST: type('[auth] Set SchoolDay Login IDP list'),
    GET_ONBOARDING_WIZARD_COMPLETED_STEPS: type('[auth] Getting Onboarding wizard completed steps'),
    GET_ONBOARDING_WIZARD_COMPLETED_STEPS_FAILED: type('[auth] Getting Onboarding wizard completed steps failed'),
    SET_ONBOARDING_WIZARD_COMPLETED_STEPS: type('[auth] Set Onboarding wizard completed steps')
};

/**
 * Authenticate.
 * @class AuthenticateAction
 * @implements {Action}
 */
export class AuthenticateAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATE;
    constructor(public payload: {username: string; password?: string; mfaCode?: string; resetCode?: string; errorMessage: string}) {}
}

/**
 * Authentication MFA First success.
 * @class AuthenticationMFAFirstSuccessAction
 * @implements {Action}
 */
export class AuthenticationMFAFirstSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATE_MFA_FIRST_SUCCESS;
    constructor(public payload?: any) {}
}

/**
 * Authentication setup MFA First success.
 * @class AuthenticationSetupMFAFirstSuccessAction
 * @implements {Action}
 */
export class AuthenticationSetupMFAFirstSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATE_SETUP_MFA_FIRST_SUCCESS;
    constructor(public payload?: any) {}
}

/**
 * SendCodeAgainAction.
 * @class AuthenticateAction
 * @implements {Action}
 */
export class SendCodeAgainAction implements Action {
    readonly type: string = AuthActionTypes.SEND_CODE_AGAIN;
    constructor(public payload?: any) {}
}

/**
 * Authentication success.
 * @class AuthenticationSuccessAction
 * @implements {Action}
 */
export class AuthenticationSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATE_SUCCESS;
    constructor(public payload?: { etxOrgId?: number }) {}
}

/**
 * Authentication error.
 * @class AuthenticationErrorAction
 * @implements {Action}
 */
export class AuthenticationErrorAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATE_ERROR;
    constructor(public payload?: {error: any}) {}
}


/**
 * Authenticated.
 * @class AuthenticatedAction
 * @implements {Action}
 */
export class AuthenticatedAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED;
    constructor(public payload?: any) {}
}

/**
 * Authenticated success.
 * @class AuthenticatedSuccessAction
 * @implements {Action}
 */
export class AuthenticatedSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_SUCCESS;
    constructor(public payload: { user: User}) {}
}

/**
 * Authenticated error.
 * @class AuthenticatedErrorAction
 * @implements {Action}
 */
export class AuthenticatedErrorAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_ERROR;
    constructor(public payload?: {error: any}) {}
}


/**
 * Authenticated user refresh.
 * @class AuthenticatedUserRefreshAction
 * @implements {Action}
 */
export class AuthenticatedUserRefreshAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_USER_REFRESH;
    constructor(public payload?: any) {}
}

/**
 * Authenticated user refresh success.
 * @class AuthenticatedUserRefreshSuccessAction
 * @implements {Action}
 */
export class AuthenticatedUserRefreshSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_USER_REFRESH_SUCCESS;
    constructor(public payload: { user: User}) {}
}

/**
 * Authenticated user refresh token.
 * @class AuthenticatedUserRefreshTokenAction
 * @implements {Action}
 */
export class AuthenticatedUserRefreshTokenAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_USER_REFRESH_TOKEN;
    constructor(public payload: void) {}
}



/**
 * Authenticated user refresh token success.
 * @class AuthenticatedUserRefreshTokenAction
 * @implements {Action}
 */
export class AuthenticatedUserRefreshTokenSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_USER_REFRESH_TOKEN_SUCCESS;
    constructor(public payload: void) {}
}



/**
 * Authenticated extra success.
 * @class AuthenticatedExtraSuccessAction
 * @implements {Action}
 */
export class AuthenticatedExtraSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_EXTRA_SUCCESS;
    constructor(public payload: { extra: any}) {}
}

/**
 * Authenticated extra error.
 * @class AuthenticatedExtraErrorAction
 * @implements {Action}
 */
export class AuthenticatedExtraErrorAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_EXTRA_ERROR;
    constructor(public payload?: { error: any}) {}
}

/**
 * Authenticated extra updated.
 * @class AuthenticatedExtraUpdatedAction
 * @implements {Action}
 */
export class AuthenticatedExtraUpdatedAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_EXTRA_UPDATED;
    constructor(public payload?: { extra: any}) {}
}

/**
 * Check accepted Privacy Policy.
 * @class CheckAcceptedPrivacyPolicyAction
 * @implements {Action}
 */
export class CheckAcceptedPrivacyPolicyAction implements Action {
    readonly type: string = AuthActionTypes.CHECK_ACCEPTED_PRIVACY_POLICY;
    constructor(public payload: { user: User}) {}
}

/**
 * Show authenticated user's Content.
 * @class ShowContentAction
 * @implements {Action}
 */
export class ShowContentAction implements Action {
    readonly type: string = AuthActionTypes.SHOW_CONTENT;
    constructor(public payload?: boolean) {}
}

/**
 * Hide authenticated user's Content.
 * @class HideContentAction
 * @implements {Action}
 */
export class HideContentAction implements Action {
    readonly type: string = AuthActionTypes.HIDE_CONTENT;
    constructor(public payload?: boolean) {}
}

/**
 * Show navigation menu.
 * @class ShowMenuAction
 * @implements {Action}
 */
export class ShowMenuAction implements Action {
    readonly type: string = AuthActionTypes.SHOW_MENU;
    constructor(public payload?: boolean) {}
}

/**
 * Hide navigation menu.
 * @class HideMenuAction
 * @implements {Action}
 */
export class HideMenuAction implements Action {
    readonly type: string = AuthActionTypes.HIDE_MENU;
    constructor(public payload?: boolean) {}
}

/**
 * Load Vendor Apps.
 * @class LoadVendorApps
 * @implements {Action}
 */
export class LoadVendorApps implements Action {
    readonly type: string = AuthActionTypes.LOAD_VENDOR_APPS;
    constructor(public payload?: { user: User, deletingAppGuid?: string}) {}
}

/**
 * Update Vendor Apps.
 * @class UpdateUserApps
 * @implements {Action}
 */
export class UpdateUserApps implements Action {
    readonly type: string = AuthActionTypes.UPDATE_USER_APPS;
    constructor(public payload: { userApplications: Application[]}) {}
}

/**
 * Authenticated update extra user preferences.
 * @class AuthenticatedUpdateExtraUserPreferencesActions
 * @implements {Action}
 */
export class AuthenticatedUpdateExtraUserPreferencesAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES;
    constructor(public payload: { preferences: any }) {}
}

/**
 * Authenticated update extra user preference item.
 * @class AuthenticatedUpdateExtraUserPreferenceItemAction
 * @implements {Action}
 */
export class AuthenticatedUpdateExtraUserPreferenceItemAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCE_ITEM;
    constructor(public payload: { key: string; userPreferenceItem: any}) {}
}

/**
 * Authenticated update extra user preferences success.
 * @class AuthenticatedUpdateExtraUserPreferencesSuccessAction
 * @implements {Action}
 */
export class AuthenticatedUpdateExtraUserPreferencesSuccessAction implements Action {
    readonly type: string = AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES_SUCCESS;
    constructor(public payload: { userPreferences: any}) {}
}

/**
 * Sign out.
 * @class SignOutAction
 * @implements {Action}
 */
export class SignOutAction implements Action {
    public type: string = AuthActionTypes.SIGN_OUT;
    constructor(public payload?: {authenticateWithETX?: boolean}) {}
}

/**
 * Sign out clear data.
 * @class SignOutAction
 * @implements {Action}
 */
export class SignOutClearDataAction implements Action {
    public type: string = AuthActionTypes.SIGN_OUT_CLEAR_DATA;
    constructor(public payload?: Action) {}
}

/**
 * Sign out success.
 * @class SignOutSuccessAction
 * @implements {Action}
 */
export class SignOutSuccessAction implements Action {
    readonly type: string = AuthActionTypes.SIGN_OUT_SUCCESS;
    constructor(public payload?: { signOutRedirectUrl?: string; authenticateWithETX?: boolean }) {}
}

/**
 * Sign out error.
 * @class SignOutErrorAction
 * @implements {Action}
 */
export class SignOutErrorAction implements Action {
    readonly type: string = AuthActionTypes.SIGN_OUT_ERROR;
    constructor(public payload?: { signOutRedirectUrl?: string; error: string }) {}
}

/**
 * Update state from Local Storage.
 * @class UpdateStateFromStorageAction
 * @implements {Action}
 */
export class UpdateStateFromStorageAction implements Action {
    readonly type: string = AuthActionTypes.UPDATE_STATE_FROM_LOCAL_STORAGE;
    constructor(public payload: { changedStateKey: string; changedStateValue: any}) {}
}

/**
 * Get User Organization.
 * @class GetUserOrganizationAction
 * @implements {Action}
 */
export class GetUserOrganizationAction implements Action {
    readonly type: string = AuthActionTypes.GET_USER_ORGANIZATION;
    constructor(public payload?: string) {}
}

/**
 * Set User Organization.
 * @class SetUserOrganizationAction
 * @implements {Action}
 */
export class SetUserOrganizationAction implements Action {
    readonly type: string = AuthActionTypes.SET_USER_ORGANIZATION;
    constructor(public payload?: DirectoryOrganization) {}
}

export class GetDistrictFeatureTogglesAction implements Action {
    readonly type = AuthActionTypes.GET_DISTRICT_FEATURE_TOGGLES;
    constructor(public payload: string) {}
}

export class SetDistrictFeatureTogglesAction implements Action {
    readonly type = AuthActionTypes.SET_DISTRICT_FEATURE_TOGGLES_SUCCESS;
    constructor(public payload: FeatureToggleModel[]) {}
}

export class GetDistrictFeatureTogglesFailedAction implements Action {
    readonly type = AuthActionTypes.GET_DISTRICT_FEATURE_TOGGLES_FAILED;
    constructor(public payload?: {error: Error}) {}
}

export class GetVendorFeatureTogglesAction implements Action {
    readonly type = AuthActionTypes.GET_VENDOR_FEATURE_TOGGLES;
    constructor(public payload: string) {}
}

export class SetVendorFeatureTogglesAction implements Action {
    readonly type = AuthActionTypes.SET_VENDOR_FEATURE_TOGGLES_SUCCESS;
    constructor(public payload: FeatureToggleModel[]) {}
}

export class GetVendorFeatureTogglesFailedAction implements Action {
    readonly type = AuthActionTypes.GET_VENDOR_FEATURE_TOGGLES_FAILED;
    constructor(public payload?: {error: Error}) {}
}

export class GetPIIShieldLevelsConfigAction implements Action {
    readonly type = AuthActionTypes.GET_PII_SHIELD_LEVELS_CONFIG;
}

export class SetPIIShieldLevelsConfigAction implements Action {
    readonly type = AuthActionTypes.SET_PII_SHIELD_LEVELS_CONFIG;
    constructor(public payload: PIIShieldLevelConfig[]) {}
}

export class GetPassportLoginIDPListAction implements Action {
    readonly type = AuthActionTypes.GET_PASSPORT_LOGIN_IDP_LIST;
}

export class SetPassportLoginIDPListAction implements Action {
    readonly type = AuthActionTypes.SET_PASSPORT_LOGIN_IDP_LIST;
    constructor(public payload: PassportLoginIDP[]) {}
}

export class GetOnboardingWizardCompletedStepsAction implements Action {
    readonly type = AuthActionTypes.GET_ONBOARDING_WIZARD_COMPLETED_STEPS;
    constructor(public payload: string) {}
}

export class SetOnboardingWizardCompletedStepsAction implements Action {
    readonly type = AuthActionTypes.SET_ONBOARDING_WIZARD_COMPLETED_STEPS;
    constructor(public payload: string[]) {}
}

export class GetOnboardingWizardCompletedStepsFailedAction implements Action {
    readonly type = AuthActionTypes.GET_ONBOARDING_WIZARD_COMPLETED_STEPS_FAILED;
    constructor(public payload?: {error: Error}) {}
}

export type Actions =
    AuthenticateAction
    | AuthenticationSuccessAction
    | AuthenticationErrorAction
    | AuthenticatedAction
    | AuthenticatedSuccessAction
    | AuthenticatedUserRefreshAction
    | AuthenticatedUserRefreshSuccessAction
    | AuthenticatedUserRefreshTokenAction
    | AuthenticatedUserRefreshTokenSuccessAction
    | AuthenticatedErrorAction
    | AuthenticatedExtraSuccessAction
    | AuthenticatedExtraErrorAction
    | AuthenticatedUpdateExtraUserPreferencesAction
    | AuthenticatedUpdateExtraUserPreferenceItemAction
    | AuthenticatedUpdateExtraUserPreferencesSuccessAction
    | AuthenticatedExtraUpdatedAction
    | CheckAcceptedPrivacyPolicyAction
    | ShowContentAction
    | HideContentAction
    | ShowMenuAction
    | HideMenuAction
    | LoadVendorApps
    | UpdateUserApps
    | SignOutAction
    | SignOutClearDataAction
    | SignOutSuccessAction
    | SignOutErrorAction
    | UpdateStateFromStorageAction
    | GetUserOrganizationAction
    | SetUserOrganizationAction
    | GetDistrictFeatureTogglesAction
    | SetDistrictFeatureTogglesAction
    | GetDistrictFeatureTogglesFailedAction
    | GetVendorFeatureTogglesAction
    | SetVendorFeatureTogglesAction
    | GetVendorFeatureTogglesFailedAction
    | GetPIIShieldLevelsConfigAction
    | SetPIIShieldLevelsConfigAction
    | GetPassportLoginIDPListAction
    | SetPassportLoginIDPListAction
    | GetOnboardingWizardCompletedStepsAction
    | SetOnboardingWizardCompletedStepsAction
    | GetOnboardingWizardCompletedStepsFailedAction;
